// Профиль — cine e omul, în cifre.
//
// La ScalpX profilul e antetul compact cu status, plăci de metrici și file. Aici e
// aceeași structură, cu o singură deosebire: bara de progres spre rangul următor arată
// cât mai e, nu doar unde ești. Fără ea, ierarhia pare o etichetă, nu o scară.

import { el, clear, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { session, initials } from '../core/session.js';
import { settings } from '../core/state.js';
import { EXCHANGE_LIST } from '../core/exchanges.js';
import { pictAvatar, pictCover } from '../ui/avatars.js';
import { alegeSiEditeaza } from '../ui/image-editor.js';
import { ia, pune } from '../core/cache.js';
import { openAuth } from '../ui/auth-modal.js';
import { toast, modal, contextMenu } from '../ui/components.js';

const ROLE_LABEL = { owner: 'Владелец', admin: 'Админ', member: '' };

function memberSince(ts) {
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export class ProfileSection {
  constructor(app) {
    this.app = app;
    // `/profile/Ghenov54/` → Ghenov54; `/profile/` → contul meu.
    const parts = location.pathname.split('/').filter(Boolean);
    // `location.pathname` da adresa codificata: un nick cu spatiu vine ca
    // „Paulina%20Demian". Fara decodare l-am fi codificat inca o data la cerere si am fi
    // cautat un om pe nume „Paulina%2520Demian", care nu exista.
    this.username = parts[1] ? decodeURIComponent(parts[1]) : null;
  }

  async mount(host) {
    this.root = el('div', { class: 'profile-section' });
    host.append(this.root);

    // La ieşirea din cont, profilul tău trebuie să dispară pe loc. Fără asta pagina
    // rămânea plină cu datele contului tocmai deconectat până la o reîncărcare —
    // arăta ca şi cum ieşirea n-ar fi funcţionat.
    //
    // „Profilul tău" nu înseamnă doar adresa `/profile/`: la fel de des ajungi pe el
    // prin nume, `/profile/Dimon54/`. Prima variantă a acestei verificări se uita doar
    // la adresă şi rata exact cazul al doilea. De aceea ţinem minte cine tocmai a
    // plecat şi comparăm cu cine e afişat. Profilul altcuiva rămâne: e public.
    this.ultimul = session.user?.username || null;
    this.offSession = bus.on('session:change', (user) => {
      const plecat = this.ultimul;
      this.ultimul = user?.username || null;
      if (user || (this.username && this.username !== plecat)) return;
      this.username = null;
      history.replaceState({}, '', '/profile/');
      this.#load();
    });

    await this.#load();
  }

  async #load() {
    const nume = this.username || session.user?.username;
    if (!nume) {
      clear(this.root).append(el('div', { class: 'profile-guest' }, [
        el('div', { class: 'ideas-empty-sub', text: 'Войдите, чтобы увидеть свой профиль.' }),
        el('button', { class: 'btn primary', text: 'Войти', onclick: () => openAuth() }),
      ]));
      return;
    }

    // Publicatiile pleaca **odata** cu profilul, nu dupa el: numele il stim din adresa
    // sau din sesiune inca de acum, deci nu avem ce astepta. Un drum dus-intors mai
    // putin pe drumul critic, si e cel mai lung dintre ele.
    this.postariInDrum = fetch(`/api/feed?author=${encodeURIComponent(nume)}`)
      .then((r) => r.json())
      .catch(() => null);

    // Daca am mai fost pe profilul asta in aceasta sesiune, il desenam pe loc din
    // memorie, iar cererea de mai jos doar il aduce la zi.
    const cheie = `profil:${nume}`;
    const stiut = ia(cheie);
    if (stiut) {
      this.#render(stiut.user, stiut.stats, stiut.follow || {}, stiut.blocked,
        stiut.badges || []);
    }

    let data;
    try {
      const resp = await fetch(`/api/community/profile/${encodeURIComponent(nume)}`);
      data = await resp.json();
      if (!resp.ok) throw new Error(data.detail);
      pune(cheie, data);
    } catch (err) {
      if (stiut) return;   // avem deja ceva pe ecran; o eroare n-are de ce sa-l stearga
      clear(this.root).append(el('div', { class: 'profile-guest' }, [
        el('div', { class: 'ideas-empty-sub', text: err.message || 'Профиль не найден' }),
      ]));
      return;
    }

    // Daca raspunsul proaspat spune exact ce era deja pe ecran, nu redesenam: a doua
    // randare ar reface tot antetul si ar cere din nou publicatiile, degeaba.
    if (stiut && JSON.stringify(stiut) === JSON.stringify(data)) return;
    this.#render(data.user, data.stats, data.follow || {}, data.blocked, data.badges || []);

    // De aici incolo nimic nu se mai asteapta unul pe altul. Inainte erau trei drumuri
    // la server unul dupa altul — profil, chei, idei — iar publicatiile plecau abia
    // dupa ele. Pe o legatura obisnuita asta inseamna o secunda in care te uiti la o
    // pagina pe jumatate desenata, degeaba: cererile n-au nicio legatura intre ele.
    //
    // Ideile se cer abia cand deschizi fila lor: e o lista pe care multi n-o deschid
    // niciodata, si n-are ce cauta pe drumul critic.
    if (this.verifiedEl) this.#loadVerified();
  }

  // ------------------------------------------- statistici verificate prin API

  /**
   * Cifrele care nu se pot inventa.
   *
   * Stau separat de cele sociale (idei, voturi) intenționat: unele arată ce ai publicat,
   * astea arată ce ai tranzacționat. Într-o asociație, a doua categorie e cea care
   * contează, iar amestecarea lor ar face-o pe prima să pară la fel de solidă.
   */
  async #loadVerified() {
    let data;
    try {
      data = await (await fetch('/api/keys')).json();
    } catch {
      return;
    }
    clear(this.verifiedEl);

    this.verifiedEl.append(el('div', { class: 'ver-head' }, [
      icon('shieldCheck', 14),
      el('h3', { text: 'Проверенная статистика' }),
      el('span', { class: 'ver-sub', text: 'Из истории сделок на бирже, а не со слов' }),
    ]));

    if (!data.enabled) {
      this.verifiedEl.append(el('p', { class: 'guest-note' }, [
        icon('warning', 11),
        el('span', { text: 'Хранение ключей выключено на сервере — обратитесь к владельцу.' }),
      ]));
      return;
    }

    const s = data.stats || {};
    if (s.connected && s.closed) {
      this.verifiedEl.append(el('div', { class: 'profile-tiles' }, [
        this.#tile('PnL за 30 дней, $', s.pnl, 'coins', s.pnl >= 0 ? 'up' : 'down'),
        this.#tile('Винрейт', s.winrate === null ? '—' : `${s.winrate}%`, 'trophy'),
        this.#tile('Профит-фактор', s.profit_factor ?? '—', 'chartLine'),
        this.#tile('Сделок закрыто', s.closed, 'bolt'),
        this.#tile('Средний плюс, $', s.avg_win, 'check', 'up'),
        this.#tile('Средний минус, $', s.avg_loss, 'close', 'down'),
      ]));
    } else if (s.connected) {
      this.verifiedEl.append(el('p', { class: 'ideas-empty-sub', text: 'Ключ подключён, закрытых сделок за 30 дней пока нет.' }));
    }

    // Cheile conectate, cu doar patru caractere vizibile.
    for (const k of data.items || []) {
      this.verifiedEl.append(el('div', { class: 'ver-key' }, [
        el('span', { class: 'ver-key-ex', text: k.exchange }),
        el('span', { class: 'ver-key-hint', text: k.hint }),
        k.last_error
          ? el('span', { class: 'ver-key-err', text: k.last_error.slice(0, 60) })
          : el('span', { class: 'ver-key-ok', text: k.last_sync ? 'синхронизирован' : 'ожидает' }),
        el('button', {
          class: 'btn', text: 'Обновить',
          onclick: async (e) => {
            e.target.disabled = true;
            try {
              await fetch('/api/keys/sync', { method: 'POST' });
              toast('Сделки обновлены', 'success');
              this.#loadVerified();
            } finally { e.target.disabled = false; }
          },
        }),
        el('button', {
          class: 'btn danger', text: 'Удалить',
          onclick: () => this.#removeKey(k.exchange),
        }),
      ]));
    }

    const conectate = new Set((data.items || []).map((k) => k.exchange));
    const libere = (data.supported || []).filter((x) => !conectate.has(x.id));
    if (libere.length) {
      this.verifiedEl.append(el('div', { class: 'ver-add' }, libere.map((x) => el('button', {
        class: 'btn', text: `Подключить ${x.label}`,
        onclick: () => this.#addKey(x),
      }))));
    }
  }

  #removeKey(exchange) {
    modal({
      title: 'Отключить биржу?',
      width: 420,
      body: el('div', { class: 'confirm-text', text: 'Вместе с ключом удалятся и импортированные сделки. Проверенная статистика обнулится.' }),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Отключить', primary: true,
          onClick: async (c) => {
            await fetch(`/api/keys/${exchange}/delete`, { method: 'POST' });
            c();
            toast('Ключ удалён', 'info');
            this.#loadVerified();
          },
        },
      ],
    });
  }

  #addKey(exchange) {
    const keyEl = el('input', { class: 'auth-input', placeholder: 'API Key' });
    const secretEl = el('input', { class: 'auth-input', type: 'password', placeholder: 'API Secret' });
    const errEl = el('div', { class: 'auth-error' });

    modal({
      title: `Ключ ${exchange.label}`,
      width: 480,
      body: el('div', { class: 'idea-form' }, [
        el('div', { class: 'ver-warn' }, [
          icon('lock', 12),
          el('span', { text: 'Создайте ключ ТОЛЬКО для чтения. Ключ с правом торговли будет отклонён — это проверяется на бирже при добавлении.' }),
        ]),
        el('label', { class: 'auth-field' }, [icon('key', 12), keyEl]),
        el('label', { class: 'auth-field' }, [icon('lock', 12), secretEl]),
        el('p', { class: 'guest-note', text: 'Секрет шифруется и никогда не показывается обратно. Импортируется только история закрытых сделок.' }),
        errEl,
      ]),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Подключить', primary: true,
          onClick: async (c, btn) => {
            errEl.textContent = 'Проверяем ключ на бирже…';
            try {
              const resp = await fetch('/api/keys', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  exchange: exchange.id, key: keyEl.value, secret: secretEl.value,
                }),
              });
              const data = await resp.json();
              if (!resp.ok) throw new Error(data.detail);
              c();
              toast(`Подключено, импортировано сделок: ${data.added}`, 'success');
              this.#loadVerified();
            } catch (err) {
              errEl.textContent = err.message || 'Не удалось подключить';
            }
          },
        },
      ],
    });
  }

  #render(user, stats, urmarire, blocat, insigne) {
    clear(this.root);
    const eu = session.user?.username === user.username;
    const st = user.status;

    // Cât s-a parcurs din treapta curentă spre următoarea.
    const start = st.level > 1 ? [0, 10, 30, 70, 150, 300][st.level - 1] : 0;
    const progres = st.next_at
      ? Math.min(100, Math.round(((user.rating - start) / (st.next_at - start)) * 100))
      : 100;

    // `Node.append(null)` inserează textul „null" — spre deosebire de `el()`, care sare
    // peste. Blocurile de mai jos sunt `null` pentru profilul altcuiva, iar pagina lui
    // arăta „nullnullnullnull" între metrici și lista de idei.
    const pune = (...copii) => this.root.append(...copii.filter(Boolean));
    pune(
      // Antetul în stil reţea socială: o bandă colorată, avatarul care intră peste ea,
      // apoi numele şi acţiunile. Culoarea benzii vine din rangul omului, nu e aleasă la
      // întâmplare — aşa profilul spune ceva înainte să citeşti o literă.
      el('div', { class: 'pf-cap' }, [
        this.bandaEl = el('div', { class: 'pf-banda' }),
        eu ? this.#butonPoza('coperta') : null,
        el('div', { class: 'pf-cap-jos' }, [
          el('div', {
            class: 'pf-av-cutie',
            // Apasarea pe cerc deschide poza mai mare. Daca omul n-are poza incarcata
            // (initiale sau desen), n-are ce deschide si nu facem nimic.
            onclick: async (e) => {
              if (e.target.closest('.pf-poza-btn')) return;
              const val = user.avatar || '';
              if (!val.startsWith('/media/') && !val.startsWith('data:')) return;
              const { deschidePoza } = await import('../ui/lightbox.js');
              deschidePoza(val, { rotund: true, alt: user.username });
            },
          }, [
            this.avatarEl = el('span', { class: 'profile-avatar pf-av', style: { background: st.color } }),
            eu ? this.#butonPoza('avatar') : null,
          ].filter(Boolean)),
          el('div', { class: 'pf-identitate' }, [
            el('div', { class: 'profile-nick-row' }, [
              el('h2', { class: 'profile-nick', text: user.username }),
              ROLE_LABEL[user.role]
                ? el('span', { class: 'chat-role', text: ROLE_LABEL[user.role] })
                : null,
            ]),
            el('div', { class: 'pf-sub' }, [
              el('span', { class: 'pf-rang', style: { color: st.color } }, [
                icon('medal', 11), el('span', { text: st.name }),
              ]),
              el('span', { class: 'pf-punct', text: '·' }),
              el('span', { text: `${user.rating} рейтинга` }),
              el('span', { class: 'pf-punct', text: '·' }),
              el('span', { text: `с ${memberSince(user.created_at)}` }),
            ]),
            // Abonatii pe rand separat, ca pe orice retea sociala: sunt cifra dupa care
            // se uita omul ca sa inteleaga daca merita citit ce scrie cineva.
            el('div', { class: 'pf-abonati' }, [
              el('button', { class: 'pf-ab', onclick: () => this.#listaAbonati(user.username, 'followers') }, [
                el('b', { text: String(urmarire.followers ?? 0) }),
                el('span', { text: ' подписчиков' }),
              ]),
              el('button', { class: 'pf-ab', onclick: () => this.#listaAbonati(user.username, 'following') }, [
                el('b', { text: String(urmarire.following ?? 0) }),
                el('span', { text: ' подписок' }),
              ]),
            ]),
            user.bio && !eu ? el('p', { class: 'pf-bio', text: user.bio }) : null,
          ]),
          // Pe profilul altcuiva, primul lucru pe care vrei să-l faci e să-i scrii.
          el('div', { class: 'pf-actiuni' }, eu ? [] : [
            this.#butonAbonare(user.username, urmarire.i_follow),
            el('button', {
              class: 'btn', onclick: () => this.#scrieLui(user.username),
            }, [icon('send', 12), el('span', { text: 'Написать' })]),
            (() => {
              const b = el('button', { class: 'btn pf-mai', title: 'Ещё' }, [icon('ellipsis', 12)]);
              b.onclick = () => contextMenu(b, [{
                label: blocat ? 'Разблокировать' : 'Заблокировать', icon: 'eyeOff',
                onClick: async () => {
                  try {
                    const d = await this.#json(`/api/social/block/${encodeURIComponent(user.username)}`);
                    toast(d.blocked ? 'Заблокирован' : 'Блокировка снята', 'success');
                    this.#load();
                  } catch (err) { toast(err.message, 'error'); }
                },
              }]);
              return b;
            })(),
          ]),
        ]),
      ]),

      // Alegerea pozei stă chiar sub cerc, unde omul se uită oricum când vrea s-o schimbe.

      // Bara de progres: arată cât mai e, nu doar unde ești.
      el('div', { class: 'profile-progress' }, [
        el('div', { class: 'profile-progress-bar' }, [
          el('span', { style: { width: `${progres}%`, background: st.color } }),
        ]),
        el('div', { class: 'profile-progress-txt' }, [
          el('span', { text: st.next ? `До «${st.next}»` : 'Высший ранг достигнут' }),
          el('span', { text: st.next_at ? `${user.rating} / ${st.next_at}` : '' }),
        ]),
      ]),

      insigne.length ? el('div', { class: 'pf-insigne' }, insigne.map((b) => el('span', {
        class: 'pf-insigna', title: b.hint,
      }, [
        el('span', { class: 'pf-insigna-e', text: b.emoji }),
        el('span', { text: b.label }),
      ]))) : null,

      el('div', { class: 'profile-tiles' }, [
        // Prima placa e cea care nu se poate negocia: verdictele date de pret.
        this.#tile('Точность', stats.accuracy?.pct === null || stats.accuracy?.pct === undefined
          ? '—' : `${stats.accuracy.pct}%`, 'shieldCheck',
        stats.accuracy?.pct >= 50 ? 'up' : stats.accuracy?.pct === null ? '' : 'down'),
        this.#tile('Идей', stats.ideas, 'lightbulb'),
        this.#tile('Отработали', stats.worked, 'check', 'up'),
        this.#tile('Не отработали', stats.failed, 'close', 'down'),
        this.#tile('Винрейт', stats.winrate === null ? '—' : `${stats.winrate}%`, 'trophy'),
        this.#tile('В работе', stats.open, 'clock'),
        this.#tile('Сообщений', stats.messages, 'comments'),
        this.#tile('Серия, дней', stats.streak ?? 0, 'fire', stats.streak >= 3 ? 'up' : ''),
      ]),

      // Setările s-au mutat în pagina lor: profilul e o carte de vizită, iar cartea de
      // vizită n-are de ce să poarte formularul de schimbat parola.
      eu && user.email_verified === false ? this.#confirmNotice() : null,
      eu ? el('button', { class: 'pf-spre-setari',
        onclick: () => this.app.navigate('/account/') }, [
        icon('userGear', 13),
        el('span', { text: 'Настройки аккаунта — пароль, биржа, аватар' }),
        icon('chevronRight', 11),
      ]) : null,
      eu ? (this.verifiedEl = el('div', { class: 'ver-card' })) : null,

      // Filele: publicaţiile omului, apoi ideile lui. Aceleaşi două lucruri pe care le
      // caută oricine intră pe un profil — ce a scris şi ce a prezis.
      el('div', { class: 'pf-file' }, [
        this.filaPost = el('button', { class: 'pf-fila active', text: 'Публикации',
          onclick: () => this.#schimbaFila('posts') }),
        this.filaIdei = el('button', { class: 'pf-fila', text: 'Идеи',
          onclick: () => this.#schimbaFila('ideas') }),
        // Salvatele stau aici, nu in lenta: e un sertar al tau, iar sertarele se tin
        // acasa. Butonul de semn de carte din lenta ar fi altfel un buton fara usa.
        eu ? (this.filaSalvate = el('button', { class: 'pf-fila', text: 'Сохранённое',
          onclick: () => this.#schimbaFila('saved') })) : null,
      ].filter(Boolean)),
      // Caseta de scris, doar pe profilul propriu: aici e locul de unde publici.
      eu ? (this.compuneEl = el('div', { class: 'pf-compune' })) : null,
      this.postariEl = el('div', { class: 'fd-list pf-postari' }),
      this.ideasEl = el('div', { class: 'ideas-list plain', style: 'display:none' }),
      this.salvateEl = el('div', { class: 'fd-list pf-postari', style: 'display:none' }),
    );

    this.numeAfisat = user.username;
    this.#incarcaPostari(user.username);
    if (eu) this.#montaCompunere(user.username);

    // Cercul se umple abia acum, după ce e în pagină: `pictAvatar` alege între poza
    // încărcată, desenul ales şi iniţiale, iar regula asta trebuie să fie una singură
    // pentru toată aplicaţia — altfel acelaşi om arată altfel în chat şi pe profil.
    pictAvatar(this.avatarEl, user, 60);
    // Coperta: desenul ales, poza incarcata, sau banda in culoarea rangului. Ultima
    // varianta trebuie sa arate aleasa, nu neterminata — de aceea nu e niciodata goala.
    pictCover(this.bandaEl, user, st.color);
  }

  /**
   * Caseta de scris de pe profil.
   *
   * E aceeasi bucata ca cea din lenta, importata de acolo - nu o a doua copie. Doua
   * casete ar insemna doua locuri de corectat la fiecare schimbare, iar ele s-ar
   * desparti la prima graba.
   */
  async #montaCompunere(nume) {
    const { FeedSection } = await import('./feed.js');
    const lenta = new FeedSection(this.app);
    this.compuneEl?.append(lenta.compunere(() => this.#incarcaPostari(nume)));
  }

  #json = async (path, body) => {
    const resp = await fetch(path, body === undefined
      ? { method: 'POST' }
      : { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body) });
    const d = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(d.detail || `Ошибка ${resp.status}`);
    return d;
  };

  /** Butonul de abonare. Starea se schimba pe loc, fara sa reincarcam pagina. */
  #butonAbonare(nume, urmaresc) {
    const b = el('button', { class: `btn${urmaresc ? '' : ' primary'}` });
    let stare = !!urmaresc;
    const deseneaza = () => {
      clear(b).append(icon(stare ? 'check' : 'plus', 12),
        el('span', { text: stare ? 'Вы подписаны' : 'Подписаться' }));
      b.classList.toggle('primary', !stare);
    };
    b.onclick = async () => {
      try {
        const d = await this.#json(`/api/social/follow/${encodeURIComponent(nume)}`);
        stare = d.follow.i_follow;
        deseneaza();
      } catch (err) { toast(err.message, 'error'); }
    };
    deseneaza();
    return b;
  }

  /** Cine urmareste pe cine. */
  async #listaAbonati(nume, fel) {
    const corp = el('div', { class: 'pf-lista-ab' }, [
      el('div', { class: 'fd-gol-mic', text: 'Загружаем…' }),
    ]);
    modal({
      title: fel === 'following' ? `Подписки · ${nume}` : `Подписчики · ${nume}`,
      width: 380, body: corp, actions: [],
    });
    try {
      const resp = await fetch(`/api/social/follows/${encodeURIComponent(nume)}?kind=${fel}`);
      const d = await resp.json();
      clear(corp);
      if (!(d.items || []).length) {
        corp.append(el('div', { class: 'fd-gol-mic', text: 'Пока никого' }));
        return;
      }
      for (const u of d.items) {
        const av = el('span', { class: 'fd-av mic' });
        pictAvatar(av, u, 30);
        corp.append(el('button', {
          class: 'pf-ab-rand',
          onclick: () => this.app.navigate(`/profile/${encodeURIComponent(u.username)}/`),
        }, [
          av,
          el('div', {}, [
            el('span', { class: 'pf-ab-nume', text: u.username }),
            el('span', { class: 'pf-ab-rang', text: u.status?.name || '' }),
          ]),
        ]));
      }
    } catch {
      clear(corp).append(el('div', { class: 'fd-gol-mic', text: 'Не удалось загрузить' }));
    }
  }

  /** Trecerea între file. */
  #schimbaFila(care) {
    this.filaPost.classList.toggle('active', care === 'posts');
    this.filaIdei.classList.toggle('active', care === 'ideas');
    this.filaSalvate?.classList.toggle('active', care === 'saved');
    this.postariEl.style.display = care === 'posts' ? '' : 'none';
    this.ideasEl.style.display = care === 'ideas' ? '' : 'none';
    this.salvateEl.style.display = care === 'saved' ? '' : 'none';
    // Salvatele se cer abia cand le ceri: pe profilul altcuiva fila nici nu exista, iar
    // pe al tau nu are rost sa aduci o lista pe care poate n-o deschizi niciodata.
    if (care === 'saved' && !this.salvateAduse) { this.salvateAduse = true; this.#incarcaSalvate(); }
    if (care === 'ideas' && !this.ideiAduse) { this.ideiAduse = true; this.#loadIdeas(this.numeAfisat); }
  }

  async #incarcaSalvate() {
    clear(this.salvateEl).append(el('div', { class: 'fd-gol-mic', text: 'Загружаем…' }));
    try {
      const d = await (await fetch('/api/feed?scope=saved')).json();
      const lista = d.items || [];
      clear(this.salvateEl);
      if (!lista.length) {
        this.salvateEl.append(el('div', { class: 'fd-gol', text: 'Пока ничего не сохранено' }));
        return;
      }
      const lenta = await this.#lentaCard();
      for (const post of lista) this.salvateEl.append(lenta.card(post));
    } catch {
      clear(this.salvateEl).append(el('div', { class: 'fd-gol', text: 'Не удалось загрузить' }));
    }
  }

  /** Instanta de lenta imprumutata pentru desenul cardurilor. */
  async #lentaCard() {
    if (!this.lentaCard) {
      const { FeedSection } = await import('./feed.js');
      this.lentaCard = new FeedSection(this.app);
    }
    return this.lentaCard;
  }

  /** Publicaţiile omului, aduse din aceeaşi lentă, filtrate după autor. */
  async #incarcaPostari(nume) {
    try {
      // Prima randare foloseste cererea pornita in `#load`; reincarcarile de dupa
      // (o stergere, o fixare) cer din nou, ca sa vada schimbarea.
      const dinDrum = this.postariInDrum;
      this.postariInDrum = null;
      const d = (dinDrum && await dinDrum)
        || await (await fetch(`/api/feed?author=${encodeURIComponent(nume)}`)).json();
      const lista = d.items || [];
      clear(this.postariEl);
      if (!lista.length) {
        this.postariEl.append(el('div', { class: 'fd-gol', text: 'Публикаций пока нет' }));
        return;
      }
      // Acelasi card ca in lenta, cu inima, idee si verdict — nu o copie simplificata.
      const lenta = await this.#lentaCard();
      // Stergerea sau fixarea unei publicatii reincarca lista profilului, nu lenta.
      lenta.laReincarcare = () => this.#incarcaPostari(nume);
      for (const p of lista) this.postariEl.append(lenta.card(p));
    } catch {
      clear(this.postariEl).append(el('div', { class: 'fd-gol', text: 'Не удалось загрузить' }));
    }
  }

  /** Mesaj direct către omul al cărui profil e deschis. */
  async #scrieLui(nume) {
    const { deschideMesaje } = await import('../ui/messages.js');
    deschideMesaje(nume);
  }

  #tile(label, value, ico, tone = '') {
    return el('div', { class: `profile-tile ${tone}` }, [
      el('span', { class: 'profile-tile-ico' }, [icon(ico, 13)]),
      el('span', { class: 'profile-tile-v', text: String(value ?? 0) }),
      el('span', { class: 'profile-tile-k', text: label }),
    ]);
  }

  /** Bara de sus când adresa n-a fost încă confirmată. */
  #confirmNotice() {
    return el('div', { class: 'ver-warn' }, [
      icon('warning', 12),
      el('span', { text: 'Почта не подтверждена. Проверьте письмо или запросите ссылку заново.' }),
      el('button', {
        class: 'btn', text: 'Прислать снова',
        onclick: async (e) => {
          e.target.disabled = true;
          try {
            const r = await session.resend();
            toast(r.sent ? 'Письмо отправлено' : 'Почта на сервере не настроена — ссылку выдаст владелец', 'info');
          } finally { e.target.disabled = false; }
        },
      }),
    ]);
  }

  /** Schimbarea parolei. Închide toate sesiunile, inclusiv pe cea curentă. */
  /**
   * Bursa şi piaţa pe care lucrezi. Se aleg o dată, aici, şi se aplică peste tot.
   *
   * Setarea exista deja — dar numai în bara screener-ului, unde se schimbă „pentru
   * acum". Iar piaţa nu se ţinea minte deloc: era scrisă fix în cod la pornire, deci
   * cine lucrează pe spot ateriza din nou pe futures după fiecare reîncărcare.
   *
   * Amândouă se salvează pe cont, nu în browser, deci alegerea te urmează şi pe alt
   * calculator. Aplicarea e imediată: aceleaşi funcţii pe care le cheamă şi butoanele
   * din screener, ca să nu existe două căi care fac acelaşi lucru altfel.
   */
  /**
   * Butonul de schimbat poza, chiar peste avatar sau peste banda.
   *
   * Setarile raman locul unde se face totul, dar aici e locul unde te uiti cand vrei
   * sa schimbi poza. Un drum de doua apasari catre alta pagina, pentru gestul cel mai
   * des facut pe un profil, e un drum in plus fara motiv.
   */
  #butonPoza(fel) {
    const b = el('button', { class: `pf-poza-btn ${fel}`,
      title: fel === 'avatar' ? 'Изменить аватар' : 'Изменить обложку' }, [icon('camera', 12)]);

    b.onclick = () => contextMenu(b, [
      {
        label: 'Загрузить фото', icon: 'upload',
        onClick: () => alegeSiEditeaza(
          fel === 'avatar'
            ? { forma: 'cerc', latimeIesire: 512, raport: 1, tinta: 110 * 1024,
              titlu: 'Аватар — подвиньте и приблизьте' }
            : { forma: 'banda', latimeIesire: 1600, raport: 4.5, tinta: 420 * 1024,
              titlu: 'Обложка — подвиньте и приблизьте' },
          async (url) => {
            try {
              await (fel === 'avatar' ? session.saveAvatar(url) : session.saveCover(url));
              toast(fel === 'avatar' ? 'Аватар обновлён' : 'Обложка обновлена', 'success');
              this.#load();
            } catch (err) { toast(err.message, 'error'); }
          },
        ),
      },
      {
        label: 'Готовые варианты', icon: 'palette',
        onClick: () => this.app.navigate('/account/'),
      },
    ]);
    return b;
  }

  #bursaCard() {
    const alesBursa = settings.get('sx_exchange', 'binance');
    const alesPiata = settings.get('sx_market', 'futures');

    const bursaEl = el('select', { class: 'set-select' },
      EXCHANGE_LIST.map((x) => el('option', {
        value: x.key, text: x.label, selected: x.key === alesBursa,
      })));
    const piataEl = el('select', { class: 'set-select' },
      [['futures', 'Фьючерсы'], ['spot', 'Спот']].map(([v, t]) => el('option', {
        value: v, text: t, selected: v === alesPiata,
      })));
    const starea = el('span', { class: 'set-hint' });

    const aplica = async () => {
      starea.textContent = 'Применяем…';
      try {
        await this.app.setExchange(bursaEl.value);
        await this.app.setMarket(piataEl.value);
        starea.textContent = 'Сохранено — скринер откроется здесь';
        toast(`${bursaEl.selectedOptions[0].text} · ${piataEl.selectedOptions[0].text}`, 'success');
      } catch (err) {
        starea.textContent = err.message || 'Не удалось применить';
      }
    };

    return el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('chartLine', 15), el('h3', { text: 'Моя биржа' })]),
      el('p', { class: 'set-card-sub', text: 'Где вы торгуете. Скринер, сетка, радар и '
        + 'плотности будут открываться на ней — выбирать каждый раз не нужно.' }),
      el('div', { class: 'set-row' }, [bursaEl, piataEl]),
      // Butonul şi mesajul pe acelaşi rând, dar într-un container care ştie de amândouă:
      // înainte mesajul era un `span` pus direct în card, deci curgea peste buton.
      el('div', { class: 'set-actions' }, [
        el('button', { class: 'btn primary', text: 'Применить', onclick: aplica }),
        starea,
      ]),
    ]);
  }

  #securityCard() {
    const oldEl = el('input', { class: 'auth-input', type: 'password', placeholder: 'Текущий пароль', autocomplete: 'current-password' });
    const newEl = el('input', { class: 'auth-input', type: 'password', placeholder: 'Новый пароль', autocomplete: 'new-password' });
    const errEl = el('div', { class: 'auth-error' });

    return el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('lock', 15), el('h3', { text: 'Пароль' })]),
      el('p', { class: 'set-card-sub', text: 'Минимум 6 символов. После смены все сессии закроются.' }),
      el('div', { class: 'idea-form-row' }, [
        el('label', { class: 'auth-field grow' }, [icon('lock', 12), oldEl]),
        el('label', { class: 'auth-field grow' }, [icon('key', 12), newEl]),
      ]),
      errEl,
      el('button', {
        class: 'btn', text: 'Сменить пароль',
        onclick: async () => {
          errEl.textContent = '';
          try {
            await session.changePassword(oldEl.value, newEl.value);
            toast('Пароль изменён — войдите заново', 'success');
            openAuth({ mode: 'login' });
          } catch (err) {
            errEl.textContent = err.message;
          }
        },
      }),
    ]);
  }

  #bioEditor(user) {
    const ta = el('textarea', {
      class: 'idea-form-text', rows: 2, maxlength: 280,
      placeholder: 'Пара слов о себе — видно другим участникам',
      value: user.bio || '',
    });
    return el('div', { class: 'profile-bio-edit' }, [
      ta,
      el('button', {
        class: 'btn', text: 'Сохранить',
        onclick: async () => {
          try {
            await session.saveProfile(ta.value);
            toast('Профиль сохранён', 'success');
          } catch (err) {
            toast(err.message || 'Не удалось сохранить', 'error');
          }
        },
      }),
    ]);
  }

  async #loadIdeas(nume) {
    if (!this.ideasEl) return;
    try {
      const data = await (await fetch(`/api/ideas?author=${encodeURIComponent(nume)}&limit=20`)).json();
      const items = data.items || [];
      if (!items.length) {
        this.ideasEl.append(el('div', { class: 'ideas-empty-sub', text: 'Идей пока нет.' }));
        return;
      }
      // Randare scurtă: cardul întreg trăiește în secțiunea de idei, aici e rezumatul.
      for (const idea of items) {
        this.ideasEl.append(el('button', {
          class: `profile-idea ${idea.status}`,
          onclick: () => this.app.navigate('/publications/'),
        }, [
          el('span', { class: 'profile-idea-sym', text: idea.symbol.replace(/USDT$/, '') }),
          el('span', { class: `idea-dir ${idea.direction}`, text: idea.direction.toUpperCase() }),
          idea.setup ? el('span', { class: 'idea-setup', text: idea.setup }) : null,
          el('span', { class: 'profile-idea-votes', text: `${idea.votes.worked} / ${idea.votes.failed}` }),
        ]));
      }
    } catch { /* profilul rămâne util și fără idei */ }
  }

  unmount() {
    this.offSession?.();
    this.root?.remove();
  }
}
